"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, Clock, ShieldCheck, Wrench, Flame, Droplets, TriangleAlert as AlertTriangle, Send, CircleCheck as CheckCircle2, MapPin, Loader as Loader2, Calendar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const serviceOptions = [{
  icon: Flame,
  title: "Water Heater Breakdown",
  description: "Same-day water heater repairs, leak fixes, and complete replacements.",
  badge: "Fast Response",
  link: "/water-heater-repair/"
}, {
  icon: Droplets,
  title: "Drain & Sewer Clearing",
  description: "High-pressure hydro jetting, camera line inspections, and severe blockages.",
  badge: "Same-Day",
  link: "/drain-cleaning/"
}, {
  icon: AlertTriangle,
  title: "Burst Pipe & Active Leaks",
  description: "Rapid isolation, pipe repairs, and non-destructive acoustic leak detection.",
  badge: "Immediate",
  link: "/pipe-repair/"
}, {
  icon: Wrench,
  title: "General Plumbing Repairs",
  description: "Fixtures, toilets, faucets, and garbage disposal troubleshooting.",
  badge: "Scheduled",
  link: "/services/"
}];
const perks = [{
  icon: Clock,
  title: "24/7 Availability",
  text: "Live local dispatchers ready day and night."
}, {
  icon: ShieldCheck,
  title: "Licensed & Insured",
  text: "Georgia master certified master technicians."
}, {
  icon: MapPin,
  title: "Jonesboro & Clayton County",
  text: "Fast arrival within our primary service area."
}];
function EmergencyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-card rounded-xl border border-border shadow-md min-h-[380px]">
        <div className="size-14 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="text-xl font-bold font-default text-foreground mb-2">
          Request Received
        </h3>
        <p className="text-sm text-muted-foreground mb-6 max-w-sm">
          {successMessage || "Our on-call technician has been notified. For immediate emergency dispatch, please call us directly."}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
          <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-accent/90">
            <a href="tel:4708479076" className="flex items-center justify-center gap-2">
              <PhoneCall className="size-4" />
              Call Dispatch
            </a>
          </Button>
          <Button variant="outline" onClick={resetForm} className="w-full">
            Submit Another Request
          </Button>
        </div>
      </div>;
  }
  return <Card className="border-border shadow-lg">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-2">
          <Badge variant="secondary" className="bg-accent/15 text-foreground font-semibold">
            Online Priority Dispatch
          </Badge>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <span className="size-2 rounded-lg bg-accent animate-pulse" />
            24/7 Live Queue
          </span>
        </div>
        <CardTitle className="text-2xl font-bold font-default text-foreground">
          Request Plumbing Service
        </CardTitle>
        <CardDescription>
          Fill out this form or call immediately for urgent flooding and burst pipes.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form onSubmit={handleSubmit} className="space-y-4">
          <FormField control={control} name="source_page" render={({
          field
        }) => <input type="hidden" {...field} value="Pipe Problems" />} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField control={control} name="full_name" rules={{
            required: "Full name is required",
            minLength: {
              value: 2,
              message: "Enter at least 2 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Your Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
            required: "Phone number is required",
            pattern: {
              value: /^[0-9+()-\s]{7,20}$/,
              message: "Enter a valid phone number"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(770) 555-0199" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField control={control} name="service_type" rules={{
            required: "Please select a service"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Service Required</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Choose service type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Emergency Repair">Emergency Repair</SelectItem>
                      <SelectItem value="Pipe Replacement">Pipe Replacement</SelectItem>
                      <SelectItem value="Repiping">Repiping</SelectItem>
                      <SelectItem value="Leak Repair">Leak Repair</SelectItem>
                      <SelectItem value="Routine Inspection">Routine Inspection</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="urgency" rules={{
            required: "Please select urgency"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Urgency Level</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select urgency" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Immediate Emergency">Immediate Emergency</SelectItem>
                      <SelectItem value="Within 24 Hours">Within 24 Hours</SelectItem>
                      <SelectItem value="This Week">This Week</SelectItem>
                      <SelectItem value="Just Exploring Options">Just Exploring Options</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={control} name="notes" render={({
          field
        }) => <FormItem>
                <FormLabel>Issue Details & Address (Optional)</FormLabel>
                <FormControl>
                  <Textarea placeholder="Describe the issue and provide your Jonesboro area address..." className="min-h-20" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="consent" rules={{
          required: "Consent is required to submit this request"
        }} render={({
          field
        }) => <FormItem className="flex flex-row items-start gap-2.5 space-y-0 rounded-lg border border-border/80 p-3 bg-muted/30">
                <FormControl>
                  <input type="checkbox" id="service-consent" checked={Boolean(field.value)} onChange={e => field.onChange(e.target.checked)} className="size-4 mt-0.5 rounded-sm border-input text-primary accent-primary cursor-pointer shrink-0" />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel htmlFor="service-consent" className="text-xs text-muted-foreground font-normal cursor-pointer leading-snug">
                    I consent to be contacted by Jonesboro Emergency Plumber via phone or SMS regarding this service inquiry.
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>} />

          {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md">
              {submitError}
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 text-base font-semibold shadow-sm">
            {isSubmitting ? <>
                <Loader2 className="animate-spin size-4 mr-2" />
                Submitting Request...
              </> : <>
                <Send className="size-4 mr-2" />
                Dispatch Plumber Now
              </>}
          </Button>
          <p className="text-center text-xs text-muted-foreground pt-1">
            No dispatch fee without prior quote approval. 100% upfront pricing.
          </p>
        </Form>
      </CardContent>
    </Card>;
}
export default function ServiceContact() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="serviceoptionscontact" data-nav="light" className="w-full bg-background text-foreground py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 lg:mb-16">
          <Badge variant="outline" className="mb-3 border-primary/30 text-primary">
            Fast Response Service
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default text-foreground tracking-tight">
            Plumbing Services & Fast Contact
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            Whether you are dealing with a 2 AM burst pipe or planning a scheduled water heater replacement, Jonesboro Emergency Plumber is ready with fully stocked mobile units.
          </p>
        </div>

        {/* Main Grid: Services Left + Contact Form Right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Service Options & Fast Call Banner */}
          <div className="lg:col-span-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {serviceOptions.map((item, i) => {
              const IconComponent = item.icon;
              return <motion.div key={item.title} data-index={i} initial={shouldReduceMotion ? {
                opacity: 1
              } : {
                opacity: 0,
                y: 16
              }} whileInView={shouldReduceMotion ? {
                opacity: 1
              } : {
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: i * 0.08
              }} className="flex flex-col justify-between bg-card p-5 rounded-xl border border-border hover:border-primary/40 shadow-xs transition-[border-color,box-shadow]">
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                          <IconComponent className="size-5" />
                        </div>
                        <Badge variant="secondary" className="text-[11px] font-medium">
                          {item.badge}
                        </Badge>
                      </div>
                      <h3 className="font-semibold font-default text-card-foreground text-base mb-1.5">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-4">
                        {item.description}
                      </p>
                    </div>
                    <Button asChild variant="outline" size="sm" className="w-full text-xs font-medium">
                      <Link to={item.link}>
                        Learn More
                      </Link>
                    </Button>
                  </motion.div>;
            })}
            </div>

            {/* Direct Callout Box */}
            <div className="p-6 rounded-xl bg-primary text-primary-foreground shadow-md relative overflow-hidden">
              <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <span className="text-xs uppercase tracking-wider font-semibold text-accent block mb-1">
                    Urgent Emergency Line
                  </span>
                  <h4 className="text-xl font-bold font-default">
                    Water Gushing or Drain Backing Up?
                  </h4>
                  <p className="text-sm text-primary-foreground/80 mt-1 max-w-sm">
                    Speak directly to our Jonesboro duty plumber in under 60 seconds.
                  </p>
                </div>
                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shrink-0 font-bold">
                  <a href="tel:4708479076" className="flex items-center gap-2">
                    <PhoneCall className="size-5" />
                    <span>(470) 847-9076</span>
                  </a>
                </Button>
              </div>
            </div>

            {/* Quick Guarantees Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              {perks.map((perk, i) => {
              const PerkIcon = perk.icon;
              return <div key={perk.title} data-index={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 border border-border/60">
                    <PerkIcon className="size-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-foreground">{perk.title}</p>
                      <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">{perk.text}</p>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-6">
            <FormProvider formId="pipe_plumbing_service_form" sectionName="service-options-contact" defaultValues={{
            full_name: "",
            phone: "",
            service_type: "Emergency Repair",
            urgency: "Immediate Emergency",
            notes: "",
            consent: false,
            source_page: "Pipe Problems"
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            service_type: {
              required: true
            },
            urgency: {
              required: true
            },
            consent: {
              required: true
            }
          }} submitText="Dispatch Plumber Now" successMessage="Thank you! Your emergency plumbing request has been dispatched to our Jonesboro field team.">
              <EmergencyForm />
            </FormProvider>
          </div>
        </div>

        {/* Bottom Direct Links Bar */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <SafeHydrate><Calendar className="size-4 text-primary" /></SafeHydrate>
            <span>Prefer to plan ahead? We also offer non-emergency scheduled estimates.</span>
          </div>
          <div className="flex items-center gap-4">
            <Button asChild variant="link" className="p-0 h-auto text-primary">
              <Link to="/problems/">View Common Problems</Link>
            </Button>
            <span className="text-border">|</span>
            <Button asChild variant="link" className="p-0 h-auto text-primary">
              <Link to="/contact/">Full Contact Page</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}